import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { treatmentAPI } from '../services/api';

function ReminderList() {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState({ treatments: [], sessions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('treatments');

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const response = await treatmentAPI.getUpcomingReminders();
      if (response.data.success) {
        setReminders(response.data.data);
      } else {
        setError('Failed to load reminders');
      }
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPatientDetails = (patientId) => {
    if (patientId) {
      navigate(`/patient/${patientId}`);
    }
  };

  const handleViewTreatmentDetails = (treatmentId) => {
    if (treatmentId) {
      navigate(`/treatment/${treatmentId}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemainingText = (dateString) => {
    if (!dateString) return '-';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);
    
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return <span style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>Today</span>;
    } else if (diffDays === 1) {
      return <span style={{ color: 'var(--warning-color)', fontWeight: 'bold' }}>Tomorrow</span>;
    } else if (diffDays < 0) {
      return <span style={{ color: 'var(--error-color)', fontWeight: 'bold' }}>{Math.abs(diffDays)} day(s) overdue</span>;
    } else {
      return <span style={{ color: 'var(--success-color)', fontWeight: 'bold' }}>In {diffDays} days</span>;
    }
  };

  return (
    <div className="card">
      <h2>⏰ Reminder - Upcoming Treatments & Sessions</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        List of ongoing client treatments and sessions scheduled in the next 3 days.
      </p>

      {error && <div className="error-message">❌ {error}</div>}

      <div className="tab-menu" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
        <button 
          className={`btn ${activeSubTab === 'treatments' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveSubTab('treatments')}
          style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
        >
          📅 Upcoming Treatment Plans ({reminders.treatments?.length || 0})
        </button>
        <button 
          className={`btn ${activeSubTab === 'sessions' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveSubTab('sessions')}
          style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
        >
          ⏰ Upcoming Sessions ({reminders.sessions?.length || 0})
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading reminders...</div>
      ) : activeSubTab === 'treatments' ? (
        reminders.treatments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            🎉 No upcoming treatment plan reminders for the next 3 days!
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Patient ID</th>
                  <th>Patient Name</th>
                  <th>Treatment Type</th>
                  <th>Scheduled Date</th>
                  <th>Status</th>
                  <th>Time Remaining</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reminders.treatments.map(treatment => (
                  <tr key={treatment.id} onClick={() => handleViewPatientDetails(treatment.patient?.id)} style={{ cursor: 'pointer' }}>
                    <td><strong>{treatment.patient?.patientId || '-'}</strong></td>
                    <td>{treatment.patient?.firstName} {treatment.patient?.lastName}</td>
                    <td>{treatment.treatmentType}</td>
                    <td>{formatDate(treatment.treatmentDate)}</td>
                    <td>
                      <span className={`chip status-${treatment.status.toLowerCase().replace(' ', '-')}`} style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        backgroundColor: treatment.status === 'In Progress' ? '#fff3e0' : '#e3f2fd',
                        color: treatment.status === 'In Progress' ? '#ef6c00' : '#1565c0'
                      }}>
                        {treatment.status}
                      </span>
                    </td>
                    <td>{getDaysRemainingText(treatment.treatmentDate)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleViewTreatmentDetails(treatment.id)}
                        style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', marginRight: '0.5rem' }}
                      >
                        View Treatment
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleViewPatientDetails(treatment.patient?.id)}
                        style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                      >
                        Patient Profile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        reminders.sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            🎉 No upcoming session reminders for the next 3 days!
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Patient ID</th>
                  <th>Patient Name</th>
                  <th>Treatment Type</th>
                  <th>Session Info</th>
                  <th>Next Session Date</th>
                  <th>Time Remaining</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reminders.sessions.map(session => (
                  <tr key={session.id} onClick={() => handleViewPatientDetails(session.treatment?.patient?.id)} style={{ cursor: 'pointer' }}>
                    <td><strong>{session.treatment?.patient?.patientId || '-'}</strong></td>
                    <td>{session.treatment?.patient?.firstName} {session.treatment?.patient?.lastName}</td>
                    <td>{session.treatment?.treatmentType}</td>
                    <td>Session #{session.sessionNumber}</td>
                    <td>{formatDate(session.nextSessionDate)}</td>
                    <td>{getDaysRemainingText(session.nextSessionDate)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleViewTreatmentDetails(session.treatment?.id)}
                        style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', marginRight: '0.5rem' }}
                      >
                        View Treatment
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleViewPatientDetails(session.treatment?.patient?.id)}
                        style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                      >
                        Patient Profile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <div style={{ marginTop: '2rem' }}>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/')}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

export default ReminderList;
